import PropTypes from 'prop-types';

import {Box, Heading, Skeleton, Text} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';

/**
 * Hero Banner Block (CF-01)
 *
 * @typedef {import('@chakra-ui/styled-system').SystemStyleObject} SystemStyleObject
 * @typedef {"root" | "backgroundWrapper" | "contentWrapper" | "title" | "subtitle" | "actionLinks"} HeroBannerBlockParts
 * @typedef {Partial<Record<HeroBannerBlockParts, SystemStyleObject>>} HeroBannerBlockStyles
 *
 * @param {Object} props - Component props
 * @param {Contentful.HeroBanner} props.component - HeroBanner component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {HeroBannerBlockStyles} [props.styles] - Component styles
 * @returns {React.ReactElement}
 */
export const HeroBannerBlockCF01 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {backgroundMedia, headingTitle, subtitle, cta} = component;

  // Determine which background to use (Image has priority over Video)
  const backgroundImage = backgroundMedia?.__typename === 'Image' ? backgroundMedia : null;
  const backgroundVideo = backgroundMedia?.__typename === 'Video' ? backgroundMedia : null;

  return (
    <Box {...styles.wrapper}>
      {/* Background Media Layer */}
      <Box className="hero-image-wrapper" {...styles.backgroundWrapper}>
        {backgroundImage ? (
          <Component component={backgroundImage} {...styles.backgroundMedia} />
        ) : backgroundVideo ? (
          <Component component={backgroundVideo} {...styles.backgroundMedia} />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
      </Box>

      {/* Content Overlay Layer */}
      <Box className="hero-content-wrapper" {...styles.contentWrapperOuter}>
        {!isComplete && isFetching ? (
          <Skeleton height="120px" />
        ) : (
          <Box {...styles.contentWrapper}>
            {headingTitle && <Heading {...styles.title}>{headingTitle}</Heading>}
            {subtitle && <Text {...styles.subtitle}>{subtitle}</Text>}
            <Box className="cta-wrapper" {...styles.ctaWrapper}>
              {cta && <Component component={cta} {...styles.actionLinks} />}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

HeroBannerBlockCF01.displayName = 'HeroBannerBlockCF01';
HeroBannerBlockCF01.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
